import type { WorkflowProviderProps, WorkflowState } from '../types/workflow-types';
import React, { useState } from 'react';
import { WorkflowContext } from './WorkflowContext';

const baseUrl = import.meta.env.VITE_APP_ENV === 'production' ? import.meta.env.VITE_BASE_URL : 'http://localhost:8000';
const wsUrl = import.meta.env.VITE_APP_ENV === 'production' ? import.meta.env.VITE_BASE_URL_WS : 'ws://localhost:8000';

console.log(`Using base URL: ${baseUrl}`);
console.log(`Using WebSocket URL: ${wsUrl}`);

export const WorkflowProvider: React.FC<WorkflowProviderProps> = ({ children }) => {
    const [workflowState, setWorkflowState] = useState<WorkflowState>({
        isActive: false,
        threadId: null,
        currentStage: 'idle',
        status: 'idle',
        progress: 0,
        preview: null,
        error: null,
        finalContent: null,
        websocket: null
    });

    const getGreeting = (): string => {
        const currentHour = new Date().getHours();

        if (currentHour >= 5 && currentHour < 12) {
            return "Good morning AK! Ready to create some impactful content today?";
        } else if (currentHour >= 12 && currentHour < 17) {
            return "Good afternoon Sir! Let's craft something meaningful.";
        } else if (currentHour >= 17 && currentHour < 21) {
            return "Good evening AK! Time to share some wisdom.";
        } else {
            return "Working late tonight Sir? Let's create something inspiring.";
        }
    };

    const [greeting] = useState<string>(getGreeting());

    const fetchCurrentContent = async () => {
        if (!workflowState.threadId) {
            throw new Error('No active workflow');
        }

        try {
            const response = await fetch(`${baseUrl}/api/workflow/${workflowState.threadId}/content`);
            if (!response.ok) {
                throw new Error(`Failed to fetch content: ${response.status}`);
            }
            const content = await response.json();
            return content;
        } catch (error) {
            console.error('Error fetching current content:', error);
            throw error;
        }
    };

    const startWorkflow = async (customTopic: string | null = null): Promise<void> => {
        try {
            setWorkflowState(prev => ({
                ...prev,
                error: null,
                finalContent: null
            }));

            const response = await fetch(`${baseUrl}/api/workflow/start`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({ custom_topic: customTopic })
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.detail || 'Failed to start workflow');
            }

            const data = await response.json();
            const threadId: string = data.thread_id;

            // Connect to WebSocket
            const ws = new WebSocket(`${wsUrl}/ws/workflow/${threadId}`);

            ws.onopen = () => {
                console.log('WebSocket connected');
            };

            ws.onmessage = (event) => {
                const update = JSON.parse(event.data);

                setWorkflowState(prev => ({
                    ...prev,
                    currentStage: update.stage,
                    status: update.status,
                    progress: (update.progress || 0) * 100,
                    preview: update.preview,
                    error: update.error
                }));
            };

            ws.onclose = () => {
                console.log('WebSocket disconnected');
            };

            ws.onerror = (error) => {
                console.error('WebSocket error:', error);
                setWorkflowState(prev => ({
                    ...prev,
                    error: 'Connection error occurred'
                }));
            };

            setWorkflowState(prev => ({
                ...prev,
                isActive: true,
                threadId,
                websocket: ws,
                currentStage: 'initializing',
                status: 'running',
                error: null,
                finalContent: null
            }));

        } catch (error) {
            console.error('Workflow start error:', error);
            setWorkflowState(prev => ({
                ...prev,
                error: error instanceof Error ? error.message : 'An unknown error occurred'
            }));
        }
    };

    const provideFeedback = async (feedback: string): Promise<void> => {
        try {
            const response = await fetch(`${baseUrl}/api/workflow/${workflowState.threadId}/feedback`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ feedback })
            });

            if (!response.ok) {
                throw new Error('Failed to submit feedback');
            }

            // Clear finalContent so it gets refetched after regeneration
            setWorkflowState(prev => ({
                ...prev,
                finalContent: null,
                currentStage: 'processing_feedback',
                status: 'running'
            }));

        } catch (error) {
            setWorkflowState(prev => ({
                ...prev,
                error: error instanceof Error ? error.message : 'An unknown error occurred'
            }));
        }
    };

    const approveContent = async (): Promise<void> => {
        try {
            const response = await fetch(`${baseUrl}/api/workflow/${workflowState.threadId}/approve`, {
                method: 'POST'
            });

            if (!response.ok) {
                throw new Error('Failed to approve content');
            }

            const content = await response.json();

            await navigator.clipboard.writeText(content.content);

            setWorkflowState(prev => ({
                ...prev,
                finalContent: content,
                isActive: false,
                status: 'approved'
            }));

            if (workflowState.websocket) {
                workflowState.websocket.close();
            }

        } catch (error) {
            console.error('Error approving content:', error);
            setWorkflowState(prev => ({
                ...prev,
                error: error instanceof Error ? error.message : 'An unknown error occurred'
            }));
        }
    };

    const resetWorkflow = (): void => {
        if (workflowState.websocket) {
            workflowState.websocket.close();
        }

        setWorkflowState({
            isActive: false,
            threadId: null,
            currentStage: 'idle',
            status: 'idle',
            progress: 0,
            preview: null,
            error: null,
            finalContent: null,
            websocket: null
        });
    };

    return (
        <WorkflowContext.Provider value={{
            workflowState,
            greeting,
            startWorkflow,
            provideFeedback,
            approveContent,
            resetWorkflow,
            fetchCurrentContent
        }}>
            {children}
        </WorkflowContext.Provider>
    );
};